
/* eslint-disable  @typescript-eslint/naming-convention */
import Vue, { PropType } from 'vue';
import { between, minLength, required } from 'vuelidate/lib/validators';
import { Validation } from 'vuelidate/vuelidate';

import Modal from '@/components/modal/Modal.vue';
import { Device } from '@/interfaces/locations';
import { DeviceType } from '@/interfaces/types';
import { Controller } from '@/interfaces/controllers';

interface DeviceForm {
	name: string;
	description: string;
	deviceType: number | null;
	offset: number | string | null;
}

interface ComponentData {
	form: DeviceForm;
}

export default Vue.extend({
	components: {
		Modal,
	},

	data(): ComponentData {
		return {
			form: {
				name: '',
				description: '',
				deviceType: null,
				offset: null,
			},
		};
	},

	props: {
		device: { type: Object as PropType<Device> },
		deviceTypes: { type: Array as PropType<DeviceType[]> },
		controller: { type: Object as PropType<Controller> },
		zones: { type: Array as PropType<Device[]>, default: (): Device[] => [] },
	},

	validations: {
		form: {
			name: {
				required,
				minLength: minLength(3),
			},
			description: {},
			deviceType: {},
			offset: {
				between: between(-100, 100),
			},
		},
	},

	computed: {
		deviceTypeOptions(): { value: number | null; text: string }[] {
			const options: { value: number | null; text: string }[] = [
				{ value: null, text: this.$t('Unknown').toString() },
			];
			for (const dt of this.deviceTypes) {
				options.push({ value: dt.id, text: dt.name });
			}
			return options;
		},
	},

	methods: {
		show() {
			if (this.device) {
				this.form = this.makeForm(this.device);
			}
			this.$bvModal.show('modal-device-edit');
		},

		hide() {
			this.$bvModal.hide('modal-device-edit');
		},

		reset() {
			this.form = {
				name: '',
				description: '',
				deviceType: null,
				offset: null,
			};
			this.$v.form.$reset();
			this.$emit('reset');
		},

		async confirm() {
			this.$v.form.$touch();
			if (this.$v.form.$anyError) {
				return;
			}

			if (typeof this.form.offset === 'string') {
				this.form.offset = null;
			}

			const device: Partial<Device> = {
				id: this.device.id,
				name: this.form.name,
				description: this.form.description,
				fk_device_type: this.form.deviceType,
				light_level_offset: this.form.offset,
			};
			switch (this.device.category) {
				case 'sensors':
				case 'luminaires':
					this.$emit('modifyDevice', device, 'luminaires', 'modifyLuminaire');
					break;
				case 'switches':
					this.$emit('modifyDevice', device, this.device.category, 'modifySwitch');
					break;
			}
		},

		validateState(validation: Validation): boolean | null {
			const { $dirty, $error } = validation;
			return $dirty ? !$error : null;
		},

		makeForm(device: Device): DeviceForm {
			return {
				name: device.name,
				description: device.description,
				deviceType: device.fk_device_type,
				offset: device.light_level_offset,
			};
		},
	},
});
